// =============================================================================
// VulnTracker — Vulnerability Matches Page
// =============================================================================
import { useState } from 'react';
import { useMatches, useUpdateMatchStatus } from '@/api/hooks';
import { SeverityBadge } from '@/components/SeverityBadge';
import { Pagination } from '@/components/Pagination';
import toast from 'react-hot-toast';
import type { VulnerabilityMatch, Severity } from '@/types';

const STATUS_LABELS: Record<string, string> = {
  not_started:   'Not Started',
  in_progress:   'In Progress',
  completed:     'Completed',
  deferred:      'Deferred',
  accepted_risk: 'Accepted Risk',
};

const STATUS_COLOURS: Record<string, string> = {
  not_started:   'bg-gray-100 text-gray-700',
  in_progress:   'bg-blue-100 text-blue-700',
  completed:     'bg-green-100 text-green-700',
  deferred:      'bg-yellow-100 text-yellow-700',
  accepted_risk: 'bg-orange-100 text-orange-700',
};

function StatusPill({ status }: { status: string }) {
  const colour = STATUS_COLOURS[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colour}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function MatchesPage() {
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState('');
  const [remediationStatus, setRemediationStatus] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useMatches({
    page,
    page_size: 25,
    ...(severity && { severity }),
    ...(remediationStatus && { remediation_status: remediationStatus }),
  });

  const updateStatus = useUpdateMatchStatus();

  const handleStatusChange = async (matchId: string, newStatus: string) => {
    try {
      await updateStatus.mutateAsync({ matchId, data: { remediation_status: newStatus } });
      toast.success('Status updated');
      setEditingId(null);
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Vulnerability Matches</h1>
        <p className="mt-1 text-sm text-gray-500">
          Every CVE detected against a component in your inventory.
          Use the status dropdown to track remediation progress.
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <select
              className="input-field px-3"
              value={severity}
              onChange={(e) => { setSeverity(e.target.value); setPage(1); }}
            >
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">None / Info</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remediation Status</label>
            <select
              className="input-field px-3"
              value={remediationStatus}
              onChange={(e) => { setRemediationStatus(e.target.value); setPage(1); }}
            >
              <option value="">All statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="deferred">Deferred</option>
              <option value="accepted_risk">Accepted Risk</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              className="btn-secondary w-full"
              onClick={() => { setSeverity(''); setRemediationStatus(''); setPage(1); }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <p className="text-sm text-gray-500">
        Showing {(data?.items ?? []).length} of {data?.total ?? 0} matches
      </p>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">CVE / Advisory</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Component</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Risk Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Detected</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {(data?.items ?? []).map((match: VulnerabilityMatch) => {
                  const advisoryId = match.cve_id ?? match.ghsa_id ?? '—';
                  const isEditing = editingId === match.id;

                  return (
                    <tr key={match.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <SeverityBadge severity={match.effective_severity as Severity} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-800">
                        {advisoryId}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <span className="text-gray-700 line-clamp-2" title={match.vuln_title ?? ''}>
                          {match.vuln_title ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-medium text-gray-800">{match.component_name ?? '—'}</span>
                        {match.component_version && (
                          <span className="ml-1 text-xs text-gray-400">@{match.component_version}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-gray-700">
                        {match.risk_score.toFixed(1)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {new Date(match.matched_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <select
                              autoFocus
                              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                              defaultValue={match.remediation_status}
                              onChange={(e) => handleStatusChange(match.id, e.target.value)}
                              onBlur={() => setEditingId(null)}
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="deferred">Deferred</option>
                              <option value="accepted_risk">Accepted Risk</option>
                            </select>
                          </div>
                        ) : (
                          <button
                            className="text-left hover:opacity-80 transition-opacity"
                            title="Click to change status"
                            onClick={() => setEditingId(match.id)}
                          >
                            <StatusPill status={match.remediation_status} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!(data?.items ?? []).length && (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-gray-400">
                      No vulnerability matches found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={page}
              totalPages={data?.total_pages ?? 1}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
